/**
 * CommanderCategories - EDH deck category tracking
 *
 * Tracks important deck building categories for Commander decks:
 * Ramp, Card Draw, Removal, Board Wipes with recommended targets.
 */
import type { ReactElement } from 'react';

import clsx from 'clsx';

import InfoButton, { InfoList, InfoSection, InfoTip } from '@/components/InfoButton/InfoButton';

import type { CommanderCategory } from '../../hooks/useDeckHealth';

export interface CommanderCategoriesProps {
  categories: CommanderCategory[];
  /** Compact mode for left panel */
  compact?: boolean;
}

const STATUS_CONFIG: Record<'low' | 'good' | 'high', { barColor: string; textColor: string }> = {
  low: {
    barColor: 'bg-red-500',
    textColor: 'text-red-400',
  },
  good: {
    barColor: 'bg-emerald-500',
    textColor: 'text-emerald-400',
  },
  high: {
    barColor: 'bg-amber-500',
    textColor: 'text-amber-400',
  },
};

// Icons for each category
const CATEGORY_ICONS: Record<string, string> = {
  ramp: '⚡',
  'card-draw': '📚',
  removal: '💀',
  'board-wipes': '🌊',
  protection: '🛡️',
  recursion: '♻️',
};

function CommanderCategoriesInfo(): ReactElement {
  return (
    <>
      <InfoSection title="What are these?">
        <p>
          Commander/EDH decks benefit from certain ratios of card types. These categories track how many cards in your deck serve common
          functions, helping ensure your deck can operate smoothly in longer multiplayer games.
        </p>
      </InfoSection>

      <InfoSection title="Category guidelines">
        <InfoList
          items={[
            'Ramp (10-15): Mana dorks, rocks, and land search to accelerate',
            'Card Draw (10-15): Effects that draw or filter cards',
            'Removal (8-12): Spot removal for creatures and permanents',
            'Board Wipes (3-5): Mass removal for resetting the game',
          ]}
        />
      </InfoSection>

      <InfoSection title="How to read">
        <InfoList
          items={[
            'Green bar: Category count is within recommended range',
            'Red bar: Below minimum—consider adding more effects',
            'Amber bar: Above maximum—may have too many, consider variety',
            'The shaded zone shows the recommended range',
          ]}
        />
      </InfoSection>

      <InfoTip>
        These are general guidelines from the Command Zone and other sources. Specific strategies may need different ratios—combo decks may
        skimp on removal, while control decks may double down on it.
      </InfoTip>
    </>
  );
}

interface CategoryBarProps {
  category: CommanderCategory;
}

function CategoryBar({ category }: CategoryBarProps): ReactElement {
  const { name, count, minRecommended, maxRecommended, status, suggestion } = category;
  const config = STATUS_CONFIG[status];
  const icon = CATEGORY_ICONS[category.id] ?? '📋';

  // Calculate bar width as percentage of max (cap at 150% of max for overflow)
  const displayMax = maxRecommended * 1.5;
  const barPercent = Math.min((count / displayMax) * 100, 100);

  // Calculate the target zone (min to max as percentage of displayMax)
  const targetStart = (minRecommended / displayMax) * 100;
  const targetEnd = (maxRecommended / displayMax) * 100;

  return (
    <div className="space-y-1.5">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-sm">{icon}</span>
          <span className="text-silver text-xs font-medium">{name}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={clsx('text-sm font-semibold', config.textColor)}>{count}</span>
          <span className="text-ash text-xs">
            / {minRecommended}-{maxRecommended}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative h-2 overflow-hidden rounded-full bg-zinc-800">
        {/* Target zone indicator */}
        <div
          className="absolute inset-y-0 bg-zinc-700/50"
          style={{
            left: `${targetStart}%`,
            width: `${targetEnd - targetStart}%`,
          }}
        />

        {/* Actual count bar */}
        <div
          className={clsx('absolute inset-y-0 left-0 rounded-full transition-all duration-300', config.barColor)}
          style={{ width: `${barPercent}%` }}
        />

        {/* Min indicator line */}
        <div className="absolute inset-y-0 w-0.5 bg-zinc-500" style={{ left: `${targetStart}%` }} />
      </div>

      {/* Suggestion (if any) */}
      {suggestion && <p className={clsx('text-[10px]', config.textColor, 'opacity-80')}>{suggestion}</p>}
    </div>
  );
}

export default function CommanderCategories({ categories, compact = true }: CommanderCategoriesProps): ReactElement {
  // Calculate overall status
  const lowCount = categories.filter((c) => c.status === 'low').length;
  const goodCount = categories.filter((c) => c.status === 'good').length;

  const overallStatus = lowCount >= 2 ? 'needs-work' : lowCount === 1 ? 'almost-there' : goodCount === categories.length ? 'great' : 'good';

  const statusMessages: Record<string, { text: string; color: string }> = {
    great: { text: '✓ All categories balanced', color: 'text-emerald-400' },
    good: { text: '✓ Mostly balanced', color: 'text-emerald-400' },
    'almost-there': { text: '◐ One category needs attention', color: 'text-amber-400' },
    'needs-work': { text: '⚠ Multiple categories need work', color: 'text-red-400' },
  };

  const status = statusMessages[overallStatus];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className={clsx('text-xs font-medium', status.color)}>{status.text}</span>
        <InfoButton title="Commander Categories">
          <CommanderCategoriesInfo />
        </InfoButton>
      </div>

      {/* Category bars */}
      <div className={clsx('space-y-4', compact && 'space-y-3')}>
        {categories.map((category) => (
          <CategoryBar key={category.id} category={category} />
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 text-[10px]">
        <div className="flex items-center gap-1">
          <div className="h-2 w-2 rounded-full bg-emerald-500" />
          <span className="text-ash">Good</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-2 w-2 rounded-full bg-red-500" />
          <span className="text-ash">Low</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-2 w-2 rounded-full bg-amber-500" />
          <span className="text-ash">High</span>
        </div>
      </div>
    </div>
  );
}
